import React, { useState, useEffect } from 'react';
import { Shield, Mail, Phone, MapPin, Calendar, CheckCircle, Trash2, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all'); // all, reviewed, unreviewed

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create base64 encoded credentials for Basic Auth
      const encodedCredentials = btoa(`${credentials.username}:${credentials.password}`);
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/submissions`, {
        headers: {
          'Authorization': `Basic ${encodedCredentials}`
        }
      });
      
      if (response.status === 200) {
        setAuthenticated(true);
        setSubmissions(response.data);
        toast.success('Login successful');
        // Store credentials for future requests
        sessionStorage.setItem('adminAuth', encodedCredentials);
      }
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    const encodedCredentials = sessionStorage.getItem('adminAuth');
    if (!encodedCredentials) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/submissions`, {
        headers: {
          'Authorization': `Basic ${encodedCredentials}`
        }
      });
      setSubmissions(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem('adminAuth');
        toast.error('Session expired. Please login again.');
      }
    }
  };

  const markAsReviewed = async (id) => {
    const encodedCredentials = sessionStorage.getItem('adminAuth');
    
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/submissions/${id}/review`,
        {},
        {
          headers: {
            'Authorization': `Basic ${encodedCredentials}`
          }
        }
      );
      
      toast.success('Marked as reviewed');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to update submission');
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    const encodedCredentials = sessionStorage.getItem('adminAuth');
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/submissions/${id}`, {
        headers: {
          'Authorization': `Basic ${encodedCredentials}`
        }
      });
      
      toast.success('Submission deleted');
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setAuthenticated(false);
    setSubmissions([]);
    setCredentials({ username: '', password: '' });
  };

  useEffect(() => {
    const checkAuth = async () => {
      const encodedCredentials = sessionStorage.getItem('adminAuth');
      if (encodedCredentials) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/admin/submissions`, {
            headers: {
              'Authorization': `Basic ${encodedCredentials}`
            }
          });
          if (response.status === 200) {
            setAuthenticated(true);
            setSubmissions(response.data);
          }
        } catch (error) {
          sessionStorage.removeItem('adminAuth');
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const filteredSubmissions = submissions.filter(sub => {
    if (filter === 'reviewed') return sub.reviewed;
    if (filter === 'unreviewed') return !sub.reviewed;
    return true;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen pt-32 pb-20">
        <Toaster position="top-center" richColors />
        <div className="container mx-auto px-4 max-w-md">
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white text-center mb-8">Admin Login</h1>
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  required
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="w-full bg-black/30 border border-gray-600 text-white rounded-md px-4 py-3 focus:border-green-500 focus:ring-green-500"
                  placeholder="Enter username"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="w-full bg-black/30 border border-gray-600 text-white rounded-md px-4 py-3 focus:border-green-500 focus:ring-green-500"
                  placeholder="Enter password"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-green-500 hover:bg-green-600 text-black font-semibold py-3"
              >
                Login
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20">
      <Toaster position="top-center" richColors />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Admin <span className="text-green-400">Dashboard</span>
            </h1>
            <p className="text-gray-400">Manage contact form submissions</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="bg-red-500/20 border-red-500 text-red-400 hover:bg-red-500/30"
          >
            Logout
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <p className="text-gray-400 mb-2">Total Submissions</p>
            <p className="text-3xl font-bold text-white">{submissions.length}</p>
          </div>
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <p className="text-gray-400 mb-2">Reviewed</p>
            <p className="text-3xl font-bold text-green-400">
              {submissions.filter(s => s.reviewed).length}
            </p>
          </div>
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <p className="text-gray-400 mb-2">Pending</p>
            <p className="text-3xl font-bold text-yellow-400">
              {submissions.filter(s => !s.reviewed).length}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'all'
                ? 'bg-white text-black'
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            All ({submissions.length})
          </button>
          <button
            onClick={() => setFilter('unreviewed')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'unreviewed'
                ? 'bg-white text-black'
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            Unreviewed ({submissions.filter(s => !s.reviewed).length})
          </button>
          <button
            onClick={() => setFilter('reviewed')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'reviewed'
                ? 'bg-white text-black'
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            Reviewed ({submissions.filter(s => s.reviewed).length})
          </button>
        </div>

        {/* Submissions List */}
        <div className="space-y-4">
          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No submissions found
            </div>
          ) : (
            filteredSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-green-500/50 transition-all cursor-pointer"
                onClick={() => setSelectedSubmission(submission)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-white">{submission.name}</h3>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-400">{submission.age} years old</span>
                      {submission.reviewed && (
                        <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Reviewed
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-3">
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {submission.email}
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {submission.location}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(submission.submittedAt)}
                      </div>
                    </div>
                    
                    <p className="text-gray-300 line-clamp-2">{submission.message}</p>
                  </div>
                  
                  <button className="text-green-400 hover:text-green-300 ml-4">
                    <Eye className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Submission Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-3xl font-bold text-white">Submission Details</h2>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Personal Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Name</p>
                  <p className="text-white font-medium">{selectedSubmission.name}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Age</p>
                  <p className="text-white font-medium">{selectedSubmission.age}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Email</p>
                  <p className="text-white font-medium">{selectedSubmission.email}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Phone</p>
                  <p className="text-white font-medium">{selectedSubmission.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Location</p>
                  <p className="text-white font-medium">{selectedSubmission.location}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">How Found</p>
                  <p className="text-white font-medium">{selectedSubmission.howFound}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Preferred Contact</p>
                  <p className="text-white font-medium capitalize">{selectedSubmission.preferredContact}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Submitted</p>
                  <p className="text-white font-medium">{formatDate(selectedSubmission.submittedAt)}</p>
                </div>
              </div>

              {/* Interests */}
              <div>
                <p className="text-gray-400 text-sm mb-2">Interests</p>
                <div className="flex flex-wrap gap-2">
                  {selectedSubmission.interests.length > 0 ? (
                    selectedSubmission.interests.map((interest, idx) => (
                      <span
                        key={idx}
                        className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm"
                      >
                        {interest}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">None selected</span>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <p className="text-gray-400 text-sm mb-2">Message</p>
                <div className="bg-black/30 rounded-lg p-4 border border-gray-700">
                  <p className="text-white whitespace-pre-wrap">{selectedSubmission.message}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                {!selectedSubmission.reviewed && (
                  <Button
                    onClick={() => markAsReviewed(selectedSubmission.id)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-black font-semibold"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Reviewed
                  </Button>
                )}
                <Button
                  onClick={() => deleteSubmission(selectedSubmission.id)}
                  variant="outline"
                  className="bg-red-500/20 border-red-500 text-red-400 hover:bg-red-500/30"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
